import {
  createContext,
  JSXElement,
  mergeProps,
  Show,
  useContext
} from 'solid-js';
import { createToaster, CreateToasterReturn, Toaster } from '@ark-ui/solid';
import * as Toast from '~/components/ui/Toast/Component.tsx';
import { AlertTriangleIcon, CheckCircleIcon, XIcon } from 'lucide-solid';
import { getAppliedClasses } from '~/utils/commonUtils.ts';
import { getLocalizedString } from '~/i18n/utils.ts';
import { Button, ButtonProps } from '~/components/ui/Button';

const ToastContext = createContext();

type ToastContextProviderProps = {
  children: JSXElement;
};

export type ToastContextValue = {
  toast: () => CreateToasterReturn;
};

type DefaultTitleParams = {
  defaultSuccessTitle: JSXElement;
  defaultErrorTitle: JSXElement;
};

const renderToast = (toast, params: DefaultTitleParams) => {
  const ToastIcon: any =
    toast().type === 'success'
      ? CheckCircleIcon
      : toast().type === 'error'
        ? AlertTriangleIcon
        : undefined;

  return (
    <Toast.Root>
      <Toast.Title
        class={getAppliedClasses({
          success: toast().type === 'success',
          error: toast().type === 'error'
        })}
      >
        <span class={`flex items-center`}>
          <Show when={ToastIcon}>
            <ToastIcon
              width={16}
              class={`mr-1`}
            />
          </Show>
          <Show
            when={!toast().title}
            fallback={toast().title}
          >
            <Show when={toast().type === 'success'}>
              {params.defaultSuccessTitle}
            </Show>
            <Show when={toast().type === 'error'}>
              {params.defaultErrorTitle}
            </Show>
          </Show>
        </span>
      </Toast.Title>

      <Toast.Description>{toast().description}</Toast.Description>
      <Toast.CloseTrigger
        asChild={(props) => (
          <Button
            {...(props() as ButtonProps)}
            variant={'link'}
            size={'xs'}
            class={`text-fg-disabled hover:text-fg-subtle ${props().class ? props().class : ''} `}
          >
            <XIcon width={14} />
          </Button>
        )}
      />
    </Toast.Root>
  );
};

export function ToastContextProvider(
  props: ToastContextProviderProps
): JSXElement {
  const merged = mergeProps({}, props);
  const titleParams = {
    defaultSuccessTitle: getLocalizedString('toast.default_title_success'),
    defaultErrorTitle: getLocalizedString('toast.default_title_failure')
  };

  const toaster: CreateToasterReturn = createToaster({
    placement: 'top',
    duration: 3000,
    gap: 24,
    overlap: true
  });

  try {
    // Try block in case this is not wrapped in IntlProvider
    return (
      <ToastContext.Provider
        value={{
          toast: () => toaster
        }}
      >
        <Toaster toaster={toaster}>
          {(toast) => renderToast(toast, titleParams)}
        </Toaster>
        {merged.children}
      </ToastContext.Provider>
    );
  } catch (e) {
    return <>{merged.children}</>;
  }
}

export const useToast = () => useContext(ToastContext) as ToastContextValue;
